import type { PumpStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  BatteryEmptyIcon,
  BatteryOneQuarterIcon,
  BatteryHalfIcon,
  BatteryThreeQuartersIcon,
  BatteryFullIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"

import type {
  ComponentPropsWithoutRef,
  FC,
  Ref,
  SVGProps,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"

import { isBatteryLow } from "./isBatteryLow"
import styles from "./PumpStatus.module.scss"

import { DataRecency } from "src/types"

export interface Props extends ComponentPropsWithoutRef<"svg"> {
  pumpStatus: PumpStatus;
  pumpStatusRecency: DataRecency;
}

export const PumpBatteryIcon = forwardRef<SVGElement, Props>(
  function PumpBatteryIcon(props: Props, propRef: Ref<SVGElement>) {
    const {
      pumpStatus,
      pumpStatusRecency,
      ...svgProps
    } = props

    const { battery_percent: batteryPercent } = pumpStatus

    const Icon: FC<SVGProps<SVGElement>> = useMemo(
      () => {
        if (batteryPercent === null || pumpStatusRecency === DataRecency.Stale) {
          return BatteryOneQuarterIcon
        }

        if (batteryPercent > 80) {
          return BatteryFullIcon
        }

        if (batteryPercent > 60) {
          return BatteryThreeQuartersIcon
        }

        if (batteryPercent > 40) {
          return BatteryHalfIcon
        }

        if (batteryPercent > 5) {
          return BatteryOneQuarterIcon
        }

        return BatteryEmptyIcon
      },
      [
        batteryPercent,
        pumpStatusRecency,
      ],
    )

    const lowBattery = isBatteryLow(
      batteryPercent,
      pumpStatusRecency,
    )

    return (
      <Icon
        {...svgProps}
        className={classNames(
          svgProps.className,
          lowBattery && styles.lowIcon,
        )}
        ref={propRef}
      />
    )
  },
)
